import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Order } from './orderService';
import { getLotSize, formatLotQuantity } from '../utils/lotSize';


// Fonction pour formater les nombres sans espaces
const formatNumber = (num: number) => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

const convertToGrayscale = async (imagePath: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = imagePath;

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject('Canvas context non disponible');

      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      for (let i = 0; i < data.length; i += 4) {
        const avg = 0.3 * data[i] + 0.59 * data[i + 1] + 0.11 * data[i + 2];
        data[i] = data[i + 1] = data[i + 2] = avg; // gris
      }

      ctx.putImageData(imageData, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };

    img.onerror = reject;
  });
};

export const generateOrderPDF = async (order: Order) => {
  const doc = new jsPDF({
    orientation: 'p',
    unit: 'mm',
    format: [80, 400], // XP-90
  });

  let y = 8;

  // Logo
  try {
    const grayLogo = await convertToGrayscale('/logo.png');
    doc.addImage(grayLogo, 'PNG', 55, y - 3, 20, 10);
  } catch {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('HELLOGASSY', 40, y, { align: 'center' });
  }
  y += 12;

  // Titre principal
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('TICKET DE COMMANDE', 40, y, { align: 'center' });
  y += 6;

  // Infos commande
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(`Commande: #${order._id?.slice(-6)}`, 5, y);
  y += 4;
  doc.text(`Date: ${new Date(order.createdAt).toLocaleString('fr-FR')}`, 5, y);
  y += 6;

  // Client
  doc.text('Client:', 5, y);
  doc.text(`${order.personalInfo?.firstName || ''} ${order.personalInfo?.lastName || ''}`, 25, y);
  y += 4;
  doc.text(`Tel: ${order.personalInfo?.phone || ''}`, 25, y);
  y += 6;

  // Livraison & paiement
  doc.text('Livraison:', 5, y);
  if (order.deliveryZone?.name) {
    doc.text(order.deliveryZone.name, 25, y);
    y += 4;
    doc.text('Frais livr.:', 5, y);
    doc.text(`${formatNumber(order.deliveryZone.price || order.shippingPrice || 0)} FCFA`, 25, y);
  } else if (order.shippingPrice > 0) {
    doc.text('Livraison', 25, y);
    y += 4;
    doc.text('Frais livr.:', 5, y);
    doc.text(`${formatNumber(order.shippingPrice)} FCFA`, 25, y);
  } else {
    doc.text('Retrait magasin', 25, y);
  }
  y += 4;
  doc.text('Paiement:', 5, y);
  doc.text(order.paymentMethod || 'N/A', 25, y);
  y += 8;

  // 🔹 Tableau avec politique de lot
  const tableData = order.orderItems.map(item => {
    const lotSize = getLotSize(item.price, item.lotSize);
    const displayQty = formatLotQuantity(item.quantity, lotSize);

    const total = item.price * item.quantity * lotSize;

    return [
      item.name,
      displayQty,
      `${formatNumber(item.price)}`,
      `${formatNumber(total)}`
    ];
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const tableWidth = 72;
  const centerX = Math.max(2, (pageWidth - tableWidth) / 2);

  autoTable(doc, {
    startY: y,
    head: [['Produit', 'Qté', 'Prix', 'Total']],
    body: tableData,
    theme: 'plain',
    styles: {
      fontSize: 10,
      lineColor: 180,
      lineWidth: 0.1,
      textColor: 20,
    },
    headStyles: {
      fillColor: [0, 0, 0],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      halign: 'center',
      fontSize: 10,
    },
    columnStyles: {
      0: { cellWidth: 30 },
      1: { cellWidth: 17, halign: 'center' },
      2: { cellWidth: 12, halign: 'right' },
      3: { cellWidth: 13, halign: 'right' },
    },
    margin: { left: 0 },
  });

  const finalY = (doc as any).lastAutoTable.finalY + 6;

  // Ligne
  doc.setDrawColor(0);
  doc.setLineWidth(0.3);
  doc.line(centerX, finalY - 2, centerX + tableWidth, finalY - 2);

  let totalsY = finalY + 5;
  const itemsPrice = order.itemsPrice ?? order.orderItems.reduce((sum, item) => {
    const lotSize = getLotSize(item.price, item.lotSize);
    return sum + item.price * item.quantity * lotSize;
  }, 0);
  const shippingPrice = order.shippingPrice || order.deliveryZone?.price || 0;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('Sous-total', centerX + 2, totalsY);
  doc.text(`${formatNumber(itemsPrice)} FCFA`, centerX + tableWidth - 8, totalsY, { align: 'right' });
  totalsY += 5;

  if (shippingPrice > 0 || order.deliveryZone?.name) {
    const zoneLabel = order.deliveryZone?.name
      ? `Livraison (${order.deliveryZone.name})`
      : 'Livraison';
    doc.text(zoneLabel, centerX + 2, totalsY);
    doc.text(`${formatNumber(shippingPrice)} FCFA`, centerX + tableWidth - 8, totalsY, { align: 'right' });
    totalsY += 5;
  }

  // Total général
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('TOTAL', centerX + 2, totalsY);
  doc.text(`${formatNumber(order.totalPrice)} FCFA`, centerX + tableWidth - 8, totalsY, { align: 'right' });

  // Pied de page
  const footerY = totalsY + 10;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text('Merci pour votre achat !', 40, footerY, { align: 'center' });
  doc.text('www.hellogassy.com', 40, footerY + 4, { align: 'center' });
  doc.text('+221 78 521 97 93 / +221 71 112 56 56', 40, footerY + 8, { align: 'center' });

  // Ouvrir PDF
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  const newWindow = window.open(pdfUrl, '_blank');
  if (newWindow) newWindow.focus();
  setTimeout(() => URL.revokeObjectURL(pdfUrl), 2000);

  return pdfBlob;
};


export const generateDashboardPDF = async (statistics: any) => {
    const doc = new jsPDF();

    doc.setFontSize(18);
    doc.text("Rapport Statistiques - Tableau de bord", 14, 20);

    doc.setFontSize(12);
    doc.text(`Généré le : ${new Date().toLocaleString()}`, 14, 30);

    // SECTION : Chiffres clés
    autoTable(doc, {
        startY: 40,
        head: [['Statistique', 'Valeur']],
        body: [
            ['Total Commandes', statistics.totalOrders],
            ['En attente', statistics.pendingOrders],
            ['Livrées', statistics.deliveredOrders],
            ['Revenu total', `${statistics.totalRevenue.toLocaleString("fr-FR")} FCFA`],
            ['Solde livraisons', `${(statistics.deliveryRevenue || 0).toLocaleString("fr-FR")} FCFA`],
            ['Nombre de livraisons', statistics.deliveryOrdersCount || 0],
            ['Payées', statistics.paidOrders],
            ['Non Payées', statistics.unpaidOrders],
        ]
    });

    // SECTION : Solde livraisons par zone
    if (statistics.deliveryByZone && statistics.deliveryByZone.length > 0) {
        autoTable(doc, {
            startY: (doc as any).lastAutoTable.finalY + 10,
            head: [['Zone', 'Nb livraisons', 'Solde']],
            body: [
                ...statistics.deliveryByZone.map((z: any) => [
                    z.name,
                    z.count,
                    `${z.amount.toLocaleString("fr-FR")} FCFA`
                ]),
                [
                    'Total',
                    statistics.deliveryOrdersCount || 0,
                    `${(statistics.deliveryRevenue || 0).toLocaleString("fr-FR")} FCFA`
                ]
            ]
        });
    }

    // SECTION : Produits les plus vendus
    autoTable(doc, {
        startY: (doc as any).lastAutoTable.finalY + 10,
        head: [['Produit', 'Quantité vendue']],
        body: statistics.topProducts.map((p: any) => [p.name, p.sales])
    });

    doc.save(`dashboard-statistiques.pdf`);
};


